import { Stack } from '@mui/material';
import SessionSelector from './components/SessionSelector';
import ResearchDomainSelector, { ResearchDomain } from './components/ResearchDomainSelector';
import InstrumentSelector, { Instrument } from './components/InstrumentSelector';
import TabularDataSelection from './components/TabularDataSelection';
import QueryWizardHeader from './components/QueryWizardHeader';
import { useWizardQuery } from '../../../api/dataWranglerApiSlice';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectAgeRange,
    selectConstructs,
    selectInstruments,
    selectLoading, setLoading,
} from '../../data-wrangler/dataWranglerSlice';
import RunQueryModal from '../../data-wrangler/components/QueryWizard/RunQueryModal';
import SaveWizardQuery from '../../data-wrangler/components/QueryWizard/SaveWizardQuery';
import LoadQueryModal from '../../data-wrangler/components/QueryWizard/LoadQueryModal';
import ShareQueryModal from '../../data-wrangler/components/QueryWizard/ShareQueryModal';
import { selectSelectedDatabase } from '../../common/databaseSlice';
import DownloadStartedModal from '../../data-wrangler/components/AdvanceDataWrangler/DownloadStartedModal';
import ExceededDownloadLimitModal from "../../data-wrangler/components/AdvanceDataWrangler/ExceededDownloadLimitModal";

const BREADCRUMBS_HEIGHT = 40;
const QUERY_WIZARD_HEADER_HEIGHT = 50;

export enum QueryType {
  NONE,
  SESSION,
  RESEARCH_DOMAIN,
  INSTRUMENT,
}

export enum FilterFlow {
  None,
  SessionBasedFiltering,
  DomainBasedFiltering,
  InstrumentBasedFiltering
}

export type HoverData = {
  type: QueryType;
  data: {
    sessions: string[];
    researchDomains: string[];
    instruments: string[];
  }
}

const QueryWizard = () => {

    const selectedDatabase = useSelector(selectSelectedDatabase);
    const {
        data,
        isFetching: loading
    } = useWizardQuery({ database: selectedDatabase });

    const dispatch = useDispatch();

    const [runQueryModalOpen, setRunQueryModalOpen] = useState(false);
    const [openLoadQueryModal, setOpenLoadQueryModal] = useState(false);
    const [openShareQueryModal, setOpenShareQueryModal] = useState(false);
    const [openSaveQueryModal, setOpenSaveQueryModal] = useState(false);
    const [searchFilter, setSearchFilter] = useState("");
    const [tooManySelected, setTooManySelected] = useState(false)
    const runningQuery = useSelector(selectLoading);

    const selectedSessions = useSelector(selectAgeRange);
    const selectedDomains = useSelector(selectConstructs);
    const selectedInstruments = useSelector(selectInstruments);

    const [hoverTarget, setHoverTarget] = useState<HoverData>({
        type: QueryType.NONE, data: { sessions: [], researchDomains: [], instruments: [] }
    });

    const [filterFlow, setFilterFlow] = useState<FilterFlow>(FilterFlow.None)

    const sessions = useMemo(() => {
        if (loading) return [];
        const sessions = [];
        for(const session of data?.ageGroups || []) {
            sessions.push({
                ...session,
                ...data?.visitMap[session.label]
            })
        }
        return sessions;
    }, [data, loading]
    )

    // The below transformations are required because the backend is sending inconvenient data to work with.

    // Adds the sessions and instruments associated with the domain
    const researchDomains = useMemo(() => {
        if (loading) return [];
        const domains = [];
        const domainToSessionMapping = new Map();

        // Add sessions associated with domain.
        for(const [session, value] of Object.entries(data?.visitMap || {})){
        //@ts-ignore
            const sessionDomains = value?.constructs || [];
            for(const domain of sessionDomains){
                if(!domainToSessionMapping.has(domain)){
                    domainToSessionMapping.set(domain, []);
                }
                domainToSessionMapping.get(domain).push(session);
            }
        }

        // Add instruments associated with domain.
        const domainToInstrumentsMapping = new Map();
        for(const [domain, value] of Object.entries(data?.constructs || {})){
            domainToInstrumentsMapping.set(domain, Object.keys(value || {}));
        }

        for(const domain of Object.keys(data?.constructs || {})) {
            const transformedDomain: ResearchDomain = {
                name: domain,
                sessions: domainToSessionMapping.get(domain),
                instruments:domainToInstrumentsMapping.get(domain)
            };
            domains.push(transformedDomain);
        }
        return domains;
    }, [data, loading]
    );

    // Add domains associated with the instrument
    const instruments = useMemo((): Instrument[] => {
        if (loading) return [];
        const instrumentToDomainMapping = new Map();
        const transformedInstruments = [];
        for(const [domain, values] of Object.entries(data?.constructs || {})){
        //@ts-ignore
            const domainInstruments = Object.keys(values || {});
            for(const inst of domainInstruments) {
                if(!instrumentToDomainMapping.has(inst)) {
                    instrumentToDomainMapping.set(inst, []);
                }
                instrumentToDomainMapping.get(inst).push(domain);
            }
        }
        const instruments = Object.values(data?.allInstruments || {}) as Instrument[];
        for(const instrument of instruments){
            const transformedInstrument = {
                ...instrument,
                domains: instrumentToDomainMapping.get(instrument.name),
            }
            transformedInstruments.push(transformedInstrument);
        }
        return transformedInstruments as Instrument[];
    }, [data, loading]
    )

    useEffect(() => {
        if(selectedSessions.length === 0 && selectedDomains.length === 0 && selectedInstruments.length === 0) {
            setFilterFlow(FilterFlow.None);
        }
    }, [selectedSessions, selectedDomains, selectedInstruments]);

    return (
        <>
            <Stack gap={3} height={"100%"} >
                <QueryWizardHeader
                    openLoadQueryModal={openLoadQueryModal}
                    setOpenLoadQueryModal={setOpenLoadQueryModal}
                    openShareQueryModal={openShareQueryModal}
                    setOpenShareQueryModal={setOpenShareQueryModal}
                    setSearchFilter={setSearchFilter}
                />
                <Stack
                    flexDirection={"row"}
                    maxHeight={`calc(100% - ${BREADCRUMBS_HEIGHT}px - ${QUERY_WIZARD_HEADER_HEIGHT}px)`}
                    margin={2}
                    gap={3}
                    flexGrow={1}
                >
                    <Stack
                        sx={{ minWidth: "35%", flexGrow: 1 }}
                        gap={3}
                        height={"100%"}
                    >
                        <ResearchDomainSelector
                            researchDomains={researchDomains}
                            domainToInstrumentsMapping={data?.constructs} sessions={[]}
                            hoverTarget={hoverTarget}
                            setHoverTarget={setHoverTarget}
                            filterFlow={filterFlow}
                            setFilterFlow={setFilterFlow}
                        />
                        <SessionSelector
                            sessions={sessions}
                            hoverTarget={hoverTarget}
                            setHoverTarget={setHoverTarget}
                            filterFlow={filterFlow}
                            setFilterFlow={setFilterFlow}
                        />
                    </Stack>
                    <InstrumentSelector
                        instruments={instruments}
                        runQueryModalOpen={runQueryModalOpen}
                        setRunQueryModalOpen={setRunQueryModalOpen}
                        setOpenSaveQueryModal={setOpenSaveQueryModal}
                        hoverTarget={hoverTarget}
                        setHoverTarget={setHoverTarget}
                        filterFlow={filterFlow}
                        setFilterFlow={setFilterFlow}
                        searchFilter={searchFilter}
                        setSearchFilter={setSearchFilter}
                        setTooManySelected={setTooManySelected}
                    />
                    <TabularDataSelection/>
                </Stack>
            </Stack>

            {/* Modals */}
            <RunQueryModal open={runQueryModalOpen} onClose={() => setRunQueryModalOpen(false)}/>
            {
                /*
        * Below hacky code to solve weird issue with an unopened dialog still being in the DOM
            https://github.com/mui/material-ui/issues/12937#issuecomment-1750549856
        * */
                runningQuery &&
        <DownloadStartedModal open={runningQuery} onClose={() => dispatch(setLoading(false))} />
            }
            <ExceededDownloadLimitModal
                open={tooManySelected}
                onClose={() => setTooManySelected(false)}
                instruments={selectedInstruments}
            />
            <SaveWizardQuery open={openSaveQueryModal} close={() => setOpenSaveQueryModal(false)}/>
            <LoadQueryModal open={openLoadQueryModal} setOpen={setOpenLoadQueryModal}/>
            <ShareQueryModal open={openShareQueryModal} setOpen={setOpenShareQueryModal}/>
        </>

    )
}

export default QueryWizard;